import React, { useEffect, useDeferredValue, useState } from 'react';
import { Appointment, Company, Customer, Service, Venue } from '@/types';
import {
  useAppointments,
  DataErrorBoundary,
  type AppointmentListParams
} from '@features/admin/data';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Bell,
  Database,
  Filter,
  Loader2,
  Mail,
  Smartphone,
  Edit2,
  XCircle,
  Send,
  FileText,
  ClipboardList,
  Plus
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { Pagination } from '@shared/components/common/Pagination';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { formatUkDate } from '@/lib/dates';
import { getAppointmentServiceNames } from '@/lib/appointmentServices';
import { buildMapById } from '@/lib/collections';

interface SageStatusMeta {
  label: string;
  className: string;
  hint: string;
}

function getSageStatusMeta(appointment: Appointment): SageStatusMeta {
  const status = String(appointment.sageSyncStatus || 'not_synced').toLowerCase();
  if (status === 'synced') {
    return {
      label: appointment.sageInvoiceNumber ? `Synced (${appointment.sageInvoiceNumber})` : 'Synced',
      className: 'bg-green-50 text-green-700 border border-green-100',
      hint: appointment.sageSyncedAt
        ? `Synced at ${formatUkDate(appointment.sageSyncedAt, '')}`
        : ''
    };
  }
  if (status === 'failed') {
    return {
      label: 'Failed',
      className: 'bg-red-50 text-red-700 border border-red-100',
      hint: appointment.sageLastSyncError || ''
    };
  }
  if (status === 'skipped') {
    return {
      label: 'Skipped',
      className: 'bg-amber-50 text-amber-700 border border-amber-100',
      hint: appointment.sageLastSyncError || ''
    };
  }
  return {
    label: 'Not Synced',
    className: 'bg-slate-50 text-slate-500 border border-slate-200',
    hint: ''
  };
}

export { getSageStatusMeta };

interface BookingListProps {
  services: Service[];
  venues: Venue[];
  customers: Customer[];
  companies: Company[];
  addOns: { id: string; name: string; durationMinutes?: number }[];
  canSyncToSage: boolean;
  sageJobId: string | null;
  selectedBookingIds: Set<string>;
  onPageLoaded: (items: Appointment[]) => void;
  onEdit: (appointment: Appointment) => void;
  onSendSms: (id: string) => void;
  onSendEmail: (id: string) => void;
  onSendQuestionnaire: (id: string, alreadySubmitted: boolean) => void;
  onViewCustomer: (customerId: string) => void;
  onViewCustomerSearch: (query: string) => void;
  onToggleSelection: (id: string, checked: boolean) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onOpenSageConfirm: () => void;
  onOpenSageJob: () => void;
  onNewBooking: () => void;
}

export const BookingList: React.FC<BookingListProps> = ({
  services,
  venues,
  customers,
  companies,
  addOns,
  canSyncToSage,
  sageJobId,
  selectedBookingIds,
  onPageLoaded,
  onEdit,
  onSendSms,
  onSendEmail,
  onSendQuestionnaire,
  onViewCustomer,
  onViewCustomerSearch,
  onToggleSelection,
  onToggleSelectAll,
  onOpenSageConfirm,
  onOpenSageJob,
  onNewBooking
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('');
  const [refFilter, setRefFilter] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const deferredRefFilter = useDeferredValue(refFilter);
  const deferredCustomerFilter = useDeferredValue(customerFilter);

  const addOnsById = buildMapById(addOns);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, venueFilter, dateFilter, serviceFilter, customerFilter, refFilter, sortDir]);

  const clearFilters = () => {
    setStatusFilter('all');
    setVenueFilter('all');
    setDateFilter('');
    setServiceFilter('all');
    setCustomerFilter('');
    setRefFilter('');
  };

  const hasActiveFilters =
    statusFilter !== 'all' ||
    venueFilter !== 'all' ||
    serviceFilter !== 'all' ||
    Boolean(dateFilter) ||
    Boolean(customerFilter) ||
    Boolean(refFilter);

  const canQueueSageSync = canSyncToSage && selectedBookingIds.size > 0;

  const getAddOnNames = (booking: Appointment) =>
    (booking.addOnIds || [])
      .map((id) => addOnsById.get(id)?.name)
      .filter(Boolean)
      .join(', ');

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-end mb-4 gap-4">
          <div>
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              Bookings Overview
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="px-2 text-slate-500 hover:text-red-600"
            >
              <XCircle className="w-4 h-4" /> Clear Filters
            </Button>
            {canSyncToSage && (
              <Button onClick={onOpenSageJob} variant="outline" size="sm" disabled={!sageJobId}>
                <FileText className="w-4 h-4" /> View Sage Job
              </Button>
            )}
            {canSyncToSage && (
              <Button
                onClick={onOpenSageConfirm}
                variant="dark"
                size="sm"
                disabled={!canQueueSageSync}
              >
                <Send className="w-4 h-4" /> Send to Sage
                {selectedBookingIds.size > 0 ? ` (${selectedBookingIds.size})` : ''}
              </Button>
            )}
            <Button onClick={onNewBooking} variant="primary" size="sm">
              <Plus className="w-4 h-4" /> New Booking
            </Button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Input
            type="text"
            placeholder="Booking ID, Payment ID, Company, or Opayo Ref..."
            value={refFilter}
            onChange={(e) => setRefFilter(e.target.value)}
          />
          <Input
            type="text"
            placeholder="Customer name or email..."
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
          />
          <Select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
            <option value="all">All Services</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <Select value={venueFilter} onChange={(e) => setVenueFilter(e.target.value)}>
            <option value="all">All Locations</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="noshow">No Show</option>
          </Select>
        </div>
      </div>

      <DataErrorBoundary invalidateOnRetry="appointments">
        <React.Suspense
          fallback={
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center text-slate-500">
              <Loader2 className="w-10 h-10 text-slate-200 animate-spin inline-block mr-2" />
              Loading bookings...
            </div>
          }
        >
          <BookingListInner
            page={page}
            pageSize={pageSize}
            statusFilter={statusFilter}
            venueFilter={venueFilter}
            dateFilter={dateFilter}
            serviceFilter={serviceFilter}
            customerFilter={deferredCustomerFilter}
            refFilter={deferredRefFilter}
            sortDir={sortDir}
            hasActiveFilters={hasActiveFilters}
            canSyncToSage={canSyncToSage}
            selectedBookingIds={selectedBookingIds}
            customers={customers}
            companies={companies}
            services={services}
            venues={venues}
            getAddOnNames={getAddOnNames}
            onSortDirToggle={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            sortDirIcon={
              sortDir === 'asc' ? (
                <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
              ) : (
                <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
              )
            }
            onToggleSelection={onToggleSelection}
            onToggleSelectAll={onToggleSelectAll}
            onViewCustomer={onViewCustomer}
            onViewCustomerSearch={onViewCustomerSearch}
            onEdit={onEdit}
            onSendSms={onSendSms}
            onSendEmail={onSendEmail}
            onSendQuestionnaire={onSendQuestionnaire}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onPageLoaded={onPageLoaded}
          />
        </React.Suspense>
      </DataErrorBoundary>
    </div>
  );
};

interface BookingListInnerProps {
  page: number;
  pageSize: number;
  statusFilter: string;
  venueFilter: string;
  dateFilter: string;
  serviceFilter: string;
  customerFilter: string;
  refFilter: string;
  sortDir: 'asc' | 'desc';
  hasActiveFilters: boolean;
  canSyncToSage: boolean;
  selectedBookingIds: Set<string>;
  customers: Customer[];
  companies: Company[];
  services: Service[];
  venues: Venue[];
  getAddOnNames: (booking: Appointment) => string;
  sortDirIcon: React.ReactNode;
  onSortDirToggle: () => void;
  onToggleSelection: (id: string, checked: boolean) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onViewCustomer: (customerId: string) => void;
  onViewCustomerSearch: (query: string) => void;
  onEdit: (appointment: Appointment) => void;
  onSendSms: (id: string) => void;
  onSendEmail: (id: string) => void;
  onSendQuestionnaire: (id: string, alreadySubmitted: boolean) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onPageLoaded: (items: Appointment[]) => void;
}

const BookingListInner: React.FC<BookingListInnerProps> = ({
  page,
  pageSize,
  statusFilter,
  venueFilter,
  dateFilter,
  serviceFilter,
  customerFilter,
  refFilter,
  sortDir,
  hasActiveFilters,
  canSyncToSage,
  selectedBookingIds,
  customers,
  companies,
  services,
  venues,
  getAddOnNames,
  sortDirIcon,
  onSortDirToggle,
  onToggleSelection,
  onToggleSelectAll,
  onViewCustomer,
  onViewCustomerSearch,
  onEdit,
  onSendSms,
  onSendEmail,
  onSendQuestionnaire,
  onPageChange,
  onPageSizeChange,
  onPageLoaded
}) => {
  const params: AppointmentListParams = {
    page,
    pageSize,
    status: statusFilter === 'all' ? undefined : statusFilter,
    venueId: venueFilter === 'all' ? undefined : venueFilter,
    serviceId: serviceFilter === 'all' ? undefined : serviceFilter,
    dateFrom: dateFilter || undefined,
    dateTo: dateFilter || undefined,
    customerName: customerFilter || undefined,
    q: refFilter || undefined,
    sortBy: 'date',
    sortDir
  };
  const result = useAppointments(params);
  const appointments = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  const servicesById = buildMapById(services);
  const venuesById = buildMapById(venues);

  const isPageFullySelected =
    appointments.length > 0 && appointments.every((a) => selectedBookingIds.has(a.id));

  useEffect(() => {
    onPageLoaded(appointments);
  }, [appointments, onPageLoaded]);

  const tableColumnCount = canSyncToSage ? 12 : 11;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
        <Table className="w-full min-w-[900px] text-left">
          <THead className="bg-slate-50 border-b">
            <TR>
              {canSyncToSage && (
                <TH align="center" className="w-12">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-teal-600"
                    checked={isPageFullySelected}
                    onChange={(e) => onToggleSelectAll(e.target.checked)}
                    aria-label="Select all bookings on current page"
                  />
                </TH>
              )}
              <TH>Ref</TH>
              <TH>Customer</TH>
              <TH>
                <button
                  type="button"
                  onClick={onSortDirToggle}
                  className="inline-flex items-center gap-1.5 hover:text-slate-800"
                  title="Toggle date sort"
                >
                  Service & Date {sortDirIcon}
                </button>
              </TH>
              <TH>Add-ons</TH>
              <TH>Location</TH>
              <TH align="center">Forms</TH>
              <TH>Booked By</TH>
              <TH align="center">Status</TH>
              <TH align="center">Sage</TH>
              <TH align="center">Reminder</TH>
              <TH align="center">Actions</TH>
            </TR>
          </THead>
          <TBody className="divide-y divide-slate-100">
            {appointments.length === 0 ? (
              <EmptyRow colSpan={tableColumnCount} className="p-12 text-center text-slate-500">
                {!hasActiveFilters ? (
                  <div className="flex flex-col items-center gap-2">
                    <Database className="w-10 h-10 text-slate-200" />
                    <p className="font-bold text-slate-800">Your database table is empty.</p>
                    <p className="text-xs">
                      No records found in Supabase. Use 'New Booking' to add one.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Filter className="w-10 h-10 text-slate-200" />
                    <p className="font-bold text-slate-800">No matches found.</p>
                    <p className="text-xs">Try clearing your filters to see more records.</p>
                  </div>
                )}
              </EmptyRow>
            ) : (
              appointments.map((app) => {
                const customer = customers.find(
                  (c) => c.email.toLowerCase() === (app.customerEmail || '').toLowerCase()
                );
                const company = companies.find((c) => c.id === app.companyId);
                const addonNames = getAddOnNames(app);
                const isFormDone = app.questionnaireStatus === 'submitted';
                const isNotRequired = app.questionnaireStatus === 'not_required';
                const sageMeta = getSageStatusMeta(app);

                return (
                  <TR key={app.id} className="hover:bg-slate-50">
                    {canSyncToSage && (
                      <TD align="center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 text-teal-600"
                          checked={selectedBookingIds.has(app.id)}
                          onChange={(e) => onToggleSelection(app.id, e.target.checked)}
                          aria-label={`Select booking ${app.id}`}
                        />
                      </TD>
                    )}
                    <TD className="font-mono text-xs text-slate-500">{app.id}</TD>
                    <TD>
                      <button
                        onClick={() => {
                          if (app.customerId) return onViewCustomer(app.customerId);
                          if (customer) return onViewCustomer(customer.id);
                          // No linked customer record (e.g. the appointment email no
                          // longer matches any customers row) — open the Customers tab
                          // pre-searched by name so staff can find/amend the record.
                          onViewCustomerSearch(app.customerName || app.customerEmail || '');
                        }}
                        className="font-medium hover:text-teal-600 hover:underline text-left text-slate-900"
                      >
                        {app.customerName}
                      </button>
                      {(company || app.companyName) && (
                        <div className="text-xs text-slate-500 mt-1">
                          Company: {company?.name || app.companyName}
                        </div>
                      )}
                    </TD>
                    <TD className="text-sm">
                      <div className="font-medium">
                        {getAppointmentServiceNames(app, servicesById) || 'N/A'}
                      </div>
                      <div className="text-slate-500 text-xs">
                        {formatUkDate(app.date, 'N/A')} at {app.time}
                      </div>
                    </TD>
                    <TD className="text-xs text-slate-500">{addonNames || 'None'}</TD>
                    <TD className="text-sm text-slate-600">
                      {venuesById.get(app.venueId)?.name || app.venueName || 'N/A'}
                    </TD>
                    <TD align="center">
                      {isNotRequired ? (
                        <span className="text-[10px] text-slate-300 uppercase font-bold">N/A</span>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${isFormDone ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-amber-50 text-amber-600 border border-amber-100'}`}
                        >
                          {isFormDone ? 'Done' : 'Pending'}
                        </span>
                      )}
                    </TD>
                    <TD className="text-sm text-slate-600">
                      {app.bookedByName && <div className="font-medium">{app.bookedByName}</div>}
                      {app.bookedByEmail && (
                        <div className="text-xs text-slate-400">{app.bookedByEmail}</div>
                      )}
                      {!app.bookedByName && !app.bookedByEmail && (
                        <span className="text-slate-400">-</span>
                      )}
                    </TD>
                    <TD align="center">
                      <StatusBadge status={app.status} />
                    </TD>
                    <TD align="center">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${sageMeta.className}`}
                          title={sageMeta.hint || undefined}
                        >
                          {sageMeta.label}
                        </span>
                        {app.sageLastSyncError &&
                          String(app.sageSyncStatus || '').toLowerCase() === 'failed' && (
                            <span
                              className="text-[10px] text-red-600 max-w-[180px] truncate"
                              title={app.sageLastSyncError}
                            >
                              {app.sageLastSyncError}
                            </span>
                          )}
                      </div>
                    </TD>
                    <TD align="center">
                      {app.reminderStatus === 'sent' ? (
                        <span className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-green-50 text-green-700 border border-green-100 flex items-center justify-center gap-1">
                          <Bell className="w-3 h-3" /> Sent
                        </span>
                      ) : app.reminderStatus === 'failed' ? (
                        <span
                          className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-red-50 text-red-600 border border-red-100 flex items-center justify-center gap-1"
                          title={`Retry ${app.reminderRetryCount || 0}/5`}
                        >
                          <AlertCircle className="w-3 h-3" /> Failed
                          {app.reminderRetryCount ? ` (${app.reminderRetryCount})` : ''}
                        </span>
                      ) : app.reminderStatus === 'skipped' ? (
                        <span className="text-[10px] text-slate-300 uppercase font-bold">
                          Skipped
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-[10px] uppercase font-bold bg-slate-50 text-slate-400 border border-slate-100">
                          Pending
                        </span>
                      )}
                    </TD>
                    <TD align="center">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => onEdit(app)}
                          title="Edit Booking"
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onSendSms(app.id)}
                          title="Send SMS Reminder"
                          className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                        >
                          <Smartphone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onSendEmail(app.id)}
                          title="Send Email Reminder"
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        {!isNotRequired && (
                          <button
                            onClick={() => onSendQuestionnaire(app.id, isFormDone)}
                            title={
                              isFormDone
                                ? 'Re-send Questionnaire (already submitted)'
                                : 'Send Questionnaire'
                            }
                            className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                          >
                            <ClipboardList className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </Table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
